import React, { useEffect } from 'react';
import FormCheckbox from '@bumble/app/components/FormCheckbox';
import FormInput from '@bumble/app/components/FormInput';
import { RegistrationContainer } from '@bumble/app/components/RegistrationContainer';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { zodResolver } from '@hookform/resolvers/zod';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyIcon from '@mui/icons-material/Key';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, InputAdornment, Link, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { TypeOf, literal, object, string } from 'zod';
import { useSubscriber } from '../../context/SubscriberContext';
import { Turnstile } from '@marsidev/react-turnstile';

const turnstileKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

const registerSchema = object({
  username: string().min(1, 'Email address is required').email('Email Address is invalid'),
  password: string()
    .min(8, 'Password must be more than 8 characters')
    .max(32, 'Password must be less than 32 characters')
    .refine(
      (v) => {
        return /^(?=.*[a-z]).+$/g.test(v);
      },
      { message: 'Password must have 1 lowercase letter.' },
    )
    .refine(
      (v) => {
        return /^(?=.*[A-Z]).+$/g.test(v);
      },
      { message: 'Password must have 1 uppercase letter.' },
    )
    // .refine(
    //   (v) => {
    //     return /^(?=.*[0-9]).+$/g.test(v);
    //   },
    //   { message: 'At least one digit expected' },
    // )
    .refine(
      (v) => {
        return /^(?=.*[^a-zA-Z0-9]).+$/g.test(v);
      },
      { message: 'Password must have 1 special character.' },
    ),
  terms: literal(true, {
    errorMap: () => ({ message: 'You must accept the Terms of Use.' }),
  }),
});

export type RegisterInput = TypeOf<typeof registerSchema>;

type RegisterObj = RegisterInput & {
  subscriber?: string;
  token: string;
};

export const Register = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [searchParams] = useSearchParams();
  const subscriberParam = searchParams.get('subscriber');
  const { subscriber, setSubscriber } = useSubscriber();
  const [token, setToken] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState('');
  const [verifyDialogOpen, setVerifyDialogOpen] = React.useState(false);

  useEffect(() => {
    if (subscriberParam) {
      setSubscriber(subscriberParam);
    }
  }, [setSubscriber, subscriberParam]);

  const methods = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
  });

  const { mutate, isLoading } = useMutation(api.createAccountApiV1CreateAccountPost, {
    onSuccess() {
      setVerifyDialogOpen(true);
      if (subscriber) {
        setSubscriber(null);
      }
    },
    onError: apiError,
  });
  const {
    reset,
    handleSubmit,
    formState: { isSubmitSuccessful },
  } = methods;

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const onSubmitHandler: SubmitHandler<RegisterInput> = (values) => {
    if (!token) {
      setStatus('Please wait for verification...');
      return;
    }
    const registerObj: RegisterObj = { ...values, token };
    if (subscriber) {
      registerObj.subscriber = subscriber;
    }
    mutate(registerObj);
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <RegistrationContainer>
      <Typography variant="h3" component="h1" mb={2}>
        Join us!
      </Typography>
      <Typography variant="body1" component="p" py={2} mb={2}>
        Enter your credentials to continue.
      </Typography>
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmitHandler)}
          noValidate
          autoComplete="off"
        >
          <FormInput
            id="username"
            name="username"
            label="Email Address"
            type="text"
            startAdornment={
              <InputAdornment position="start">
                <EmailOutlinedIcon />
              </InputAdornment>
            }
          />
          <FormInput
            id="password"
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            startAdornment={
              <InputAdornment position="start">
                <KeyIcon />
              </InputAdornment>
            }
          />
          <Box my={2}>
            <FormCheckbox
              id="terms"
              name="terms"
              label={
                <>
                  I acknowledge that I agree to the <br />
                  <Link
                    href="https://www.bumblebeenet.com/terms-conditions"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms of Use
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="https://www.bumblebeenet.com/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </Link>
                  .
                </>
              }
            />
          </Box>
          <Box my={2}>
            <Turnstile
              siteKey={turnstileKey}
              onSuccess={(token) => {
                setStatus('');
                setToken(token);
              }}
              onExpire={() => setToken(null)}
              onError={() => setToken(null)}
              options={{ theme: "auto" }}
            />
            {status && <p>{status}</p>}
          </Box>
          <Box mt={4}>
            <Button variant="contained" size="large" fullWidth type="submit" disabled={isLoading}>
              Register
            </Button>
          </Box>
        </Box>
      </FormProvider>
      <Box py={6}>
        <Divider />
      </Box>
      <Button size="small" variant="text" component={RouterLink} to="/login">
        Already have an account?
      </Button>
      <Dialog open={verifyDialogOpen}>
        <DialogTitle>Check your email</DialogTitle>
        <DialogContent>
          <Typography>
            Your account has been created. Please check your email inbox and click the verification
            link to validate the email address you used to register.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setVerifyDialogOpen(false);
              navigate('/login');
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </RegistrationContainer>
  );
};
